import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import {
  getAccountDetailByAccountNumber,
} from "../../creditAccounts/logic/slice";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import { updateCreditAccount } from "../../creditAccounts/logic/thunks";

import { updateCreditAccountDetails } from "../logic/accountDetailsThunk";

import { getProducts } from "../../configs/logic/Slice";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { CREDIT_FACILITY_OPTIONS } from "../../../api/creditAccounts";
import { CREDIT_FACILITY_LABELS } from "../../../api/credit_accounts";
import { TYPE_OF_INTEREST_LABELS, TypeOfInterest } from "../../../api/credit_account_details";
import { showErrorToast } from "../../../lib/toast";

interface AccountDetailsProps {
  accountNumber: string;
}

const TYPE_OF_INTEREST_OPTIONS: OptionNameValuePair<TypeOfInterest>[] = [
  {
    name: "Fixed",
    value: TypeOfInterest.FIXED,
  },
  {
    name: "Floating",
    value: TypeOfInterest.FLOATING,
  },
];

const AccountDetails: React.FC<AccountDetailsProps> = ({
  accountNumber,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const accountDetails = useAppSelector((state) =>
    getAccountDetailByAccountNumber(state, accountNumber)
  );

  const products = useAppSelector(getProducts);

  const account = accountDetails?.account;
  const accountOverview = accountDetails?.accountOverview;

  const creditAccountDetails =
    accountOverview?.credit_account_details;

  const selectedAccountNumber =
    account?.account_number ??
    accountOverview?.account_number;
  const extAccountNumber = account!.ext_account_number;

  if (!selectedAccountNumber) {
    return null;
  }

  const account_name = account?.account_name;
  const product = account?.product;
  const credit_facility = account?.credit_facility;

  const original_sanction_amount = creditAccountDetails?.original_sanction_amount;

  const account_opening_date = creditAccountDetails?.account_opening_date;

  const tenure_in_months = creditAccountDetails?.tenure_in_months;

  const rate_of_interest = creditAccountDetails?.rate_of_interest;

  const type_of_interest = creditAccountDetails?.type_of_interest;

  const productOptions: OptionNameValuePair<string>[] =
    products.map((productName) => ({
      name: productName,
      value: productName,
    }));

  const handleSave = async (
    fieldKey: string,
    value: string | number | null
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const creditAccountFieldKeys = [
        "account_name",
        "product",
        "credit_facility",
      ];

      if (creditAccountFieldKeys.includes(fieldKey)) {
        await dispatch(
          updateCreditAccount({
            case_id: caseId!,
            account_number: selectedAccountNumber,
            request: {
              [fieldKey]: String(value),
            },
          })
        ).unwrap();

        setEditingField(null);
        return true;
      }

      const creditAccountDetailsFieldKeys = [
        "original_sanction_amount",
        "account_opening_date",
        "tenure_in_months",
        "rate_of_interest",
        "type_of_interest",
      ];

      if (creditAccountDetailsFieldKeys.includes(fieldKey)) {
        let parsedValue: string | number | null = value;

        if (fieldKey === "account_opening_date") {
          parsedValue = value === null ? "" : String(value);
        } else if (fieldKey === "type_of_interest") {
          parsedValue = value ? (String(value) as TypeOfInterest) : null;
        } else if (
          fieldKey === "original_sanction_amount" ||
          fieldKey === "tenure_in_months" ||
          fieldKey === "rate_of_interest"
        ) {
          parsedValue = value === null ? null : Number(value);
        }

        await dispatch(
          updateCreditAccountDetails({
            caseId: caseId!,
            accountNumber: selectedAccountNumber,
            request: {
              [fieldKey]: parsedValue,
            },
          })
        ).unwrap();

        setEditingField(null);
        return true;
      }

      return false;
    } catch (error) {
      showErrorToast("Failed to update account details. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Account Details">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="Account Number"
          value={extAccountNumber}
          type="text"
          isEditable={false}
        />

        <EditableField
          label="Name"
          value={account_name ?? null}
          type="text"
          isEditable={true}
          isEditing={editingField === "account_name"}
          onEditStart={() => setEditingField("account_name")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("account_name", value)
          }
          disabled={isSaving}
        />

        <EditableField
          label="Product Type"
          value={product ?? null}
          type="select"
          isEditable={true}
          options={productOptions}
          isEditing={editingField === "product"}
          onEditStart={() => setEditingField("product")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("product", value)
          }
          disabled={isSaving}
        />

        <EditableField
          label="Credit Facility"
          value={credit_facility ?? null}
          type="select"
          isEditable={true}
          options={CREDIT_FACILITY_OPTIONS}
          displayValue={
            credit_facility
              ? CREDIT_FACILITY_LABELS[credit_facility]
              : "--"
          }
          isEditing={editingField === "credit_facility"}
          onEditStart={() => setEditingField("credit_facility")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("credit_facility", value)
          }
          disabled={isSaving}
        />

        <EditableField
          label="Original Sanctioned Amt. (in ₹)"
          value={original_sanction_amount ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "original_sanction_amount"}
          onEditStart={() => setEditingField("original_sanction_amount")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("original_sanction_amount", value)
          }
          disabled={isSaving}
        />

        <EditableField
          label="Account Opening Date"
          value={account_opening_date ?? null}
          type="date"
          isEditable={true}
          isEditing={editingField === "account_opening_date"}
          onEditStart={() => setEditingField("account_opening_date")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("account_opening_date", value)
          }
          disabled={isSaving}
        />

        <EditableField
          label="Tenure (months)"
          value={tenure_in_months ?? null}
          type="integer"
          isEditable={true}
          isEditing={editingField === "tenure_in_months"}
          onEditStart={() => setEditingField("tenure_in_months")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("tenure_in_months", value)
          }
          displayValue={
            tenure_in_months !== null &&
            tenure_in_months !== undefined
              ? `${tenure_in_months} months`
              : "--"
          }
          disabled={isSaving}
        />

        <EditableField
          label="Rate of Interest"
          value={rate_of_interest ?? null}
          type="float"
          isEditable={true}
          isEditing={editingField === "rate_of_interest"}
          onEditStart={() => setEditingField("rate_of_interest")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("rate_of_interest", value)
          }
          displayValue={
            rate_of_interest !== null &&
            rate_of_interest !== undefined
              ? `${rate_of_interest}% p.a.`
              : "--"
          }
          disabled={isSaving}
        />

        <EditableField
          label="Type of Interest"
          value={type_of_interest ?? null}
          type="select"
          isEditable={true}
          options={TYPE_OF_INTEREST_OPTIONS}
          displayValue={
            type_of_interest
              ? TYPE_OF_INTEREST_LABELS[type_of_interest]
              : "--"
          }
          isEditing={editingField === "type_of_interest"}
          onEditStart={() => setEditingField("type_of_interest")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("type_of_interest", value)
          }
          disabled={isSaving}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default AccountDetails;